import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { EditerIbanTiers, MutationGetListeIbansTiersArgs } from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FormValidator } from 'src/app/validators/form-validator';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';

@Component({
    selector: 'app-shared-iban-edit',
    templateUrl: './shared-iban-edit.component.html',
    styleUrls: ['./shared-iban-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedIbanEditComponent),
    standalone: false
})
export class SharedIbanEditComponent extends CustomFormControlAccess implements OnInit {
    @Input()
    label = 'Iban';

    @Coerce('boolean')
    @Input()
    isRequired = false;

    @Input()
    placeholder = 'Sélectionner un iban';

    ibanControl: FormControl;

    ibansList: EditerIbanTiers[] = [];

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService) {
        super();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    private _beneficiaryId = new BehaviorSubject<number>(null);

    get beneficiaryId(): number {
        return this._beneficiaryId.getValue();
    }

    @Input()
    set beneficiaryId(id: number) {
        this._beneficiaryId.next(id);
    }

    get beneficiaryId$(): Observable<number> {
        return this._beneficiaryId.asObservable();
    }

    validate(c: AbstractControl): Observable<ValidationErrors | null> {
        return of(this.ibanControl.valid ? null : {ibanediterror: true});
    }

    writeValue(value: number): void {
        this.ibanControl.patchValue(value);
        super.writeValue(value);
    }

    private initSubscription(): void {
        this.watch$ = this.ibanControl.valueChanges.pipe(
            tap(value => this.notifyUpdate(value))
        );

        this.watch$ = this.beneficiaryId$.pipe(
            tap(() => this.loading = true),
            switchMap(beneficiaryId => !!beneficiaryId ? this.ibansList$() : of([])),
            tap(ibansList => {
                this.ibansList = ibansList;
                let value = this.ibanControl.value;
                switch (this.ibansList.length) {
                    case 0 :
                        value = null;
                        break;
                    case 1 :
                        value = this.ibansList[0].IdRib;
                        break;
                    default :
                        if (value && !this.ibansList.some(iban => iban.IdRib === value)) {
                            value = null;
                        }
                        break;
                }
                this.ibanControl.patchValue(value || null);

                this.ibanControl[arrayNotEmpty(this.ibansList) ? 'enable' : 'disable']({emitEvent: false});
            }),
            tap(() => this.loading = false),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }


    private ibansList$(): Observable<EditerIbanTiers[]> {
        return this.graphqlService.mutateOne<'GetListeIbansTiers', MutationGetListeIbansTiersArgs>({
            mutation: gql`
                mutation GetListeIbansTiers( $contexte: ContextInfosInput, $criteres: CriteresRibsModelInput) {
                    GetListeIbansTiers( contexte: $contexte, criteres: $criteres){
                        EditerIbanTiers {
                            IdRib
                            IdBeneficiaire
                            CodePaysIban
                            CleIban
                            BBAN
                            IsParDefaut
                            IsCloture
                        }
                    }
                }`,
            variables: {
                criteres: {
                    IdBeneficiaire: this.beneficiaryId
                }
            }
        }).pipe(
            map(GetListeIbansTiers =>
                (GetListeIbansTiers?.EditerIbanTiers?.filter(({IsCloture}) => !IsCloture)) as EditerIbanTiers[] || [])
        );
    }

    ngOnInit(): void {
        const validators = [FormValidator.int];
        if (!!this.isRequired) {
            validators.push(FormValidator.isNotNull);
        }

        this.ibanControl = new FormControl(null, validators);

        this.initSubscription();
    }
}
